/**
 * Decision logic for the GlobalErrorHandler's 401 clear-tokens-and-reload branch (#30948).
 *
 * The reload exists so a user whose token went stale gets a clean re-auth. But an
 * UNCONDITIONAL reload turns any RECURRING 401 into an infinite reload loop: the page
 * renders, the failing call 401s again, the handler reloads again (live: Tracy's
 * software-security /home loop, 2026-08-28). Two rules break the loop without losing
 * the recovery behavior:
 *
 * 1. Fire-and-forget telemetry (route tracking) must never reload the app. Its failure
 *    costs one telemetry row; a reload costs the user their session state.
 * 2. At most one auto-reload per window. If a 401 recurs right after a reload, reloading
 *    again cannot fix it, so stop and let the error surface instead.
 */

export const AUTH_RELOAD_MIN_INTERVAL_MS = 60_000;
export const AUTH_RELOAD_STORAGE_KEY = 'ups-auth-401-reload-at';

/** URL fragments whose 401s must never trigger the reload path. */
const TELEMETRY_URL_FRAGMENTS = ['/api/userroute/record'];

export type Auth401ReloadReason =
  | 'reload'
  | 'telemetry-exempt'
  | 'suppressed-recent-reload';

export interface Auth401ReloadDecision {
  reload: boolean;
  reason: Auth401ReloadReason;
}

export function decideAuth401Reload(
  requestUrl: string | null | undefined,
  lastReloadAtMs: number | null,
  nowMs: number,
  minIntervalMs: number = AUTH_RELOAD_MIN_INTERVAL_MS
): Auth401ReloadDecision {
  const url = (requestUrl ?? '').toLowerCase();
  if (TELEMETRY_URL_FRAGMENTS.some((fragment) => url.includes(fragment))) {
    return { reload: false, reason: 'telemetry-exempt' };
  }

  if (lastReloadAtMs !== null && nowMs - lastReloadAtMs < minIntervalMs) {
    return { reload: false, reason: 'suppressed-recent-reload' };
  }

  return { reload: true, reason: 'reload' };
}

/** sessionStorage survives a reload in the same tab, which is exactly the loop carrier. */
export function readLastAuthReloadAt(
  storage: Storage | undefined
): number | null {
  try {
    const raw = storage?.getItem(AUTH_RELOAD_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLastAuthReloadAt(
  storage: Storage | undefined,
  nowMs: number
): void {
  try {
    storage?.setItem(AUTH_RELOAD_STORAGE_KEY, String(nowMs));
  } catch {
    // storage unavailable (private mode / blocked): the guard degrades to the old
    // behavior for this tab, never blocks the recovery reload.
  }
}
